import { useEffect, useRef } from "react";

type UseDisableMainScrollProps = {
	enabled: boolean;
	timeout?: number;
};

/** This hook will disable the main scroll after timeout */
const useDisableMainScroll = ({ enabled, timeout = 0 }: UseDisableMainScrollProps) => {
	// Create ref for document body node
	const bodyContent = document.getElementById("main-body-content");
	const bodyContentRef = useRef(bodyContent);

	// Get current vertical scroll position
	const scrollPosition = window.pageYOffset;

	// Function for assigning styles to body content ref
	const applyStyles = (styles: Partial<CSSStyleDeclaration>) => {
		if (bodyContentRef.current) {
			Object.assign(bodyContentRef.current.style, styles);
		}
	};

	// Function for assigning body styles to prevent scrolling when modal opens
	const disableScroll = () => {
		applyStyles({
			position: "fixed",
			top: `-${scrollPosition}px`,
			left: "0",
			right: "0",
			bottom: "0",
			overflow: "hidden",
		});
	};

	// Function for assigning body styles when modal is closed and scrolling should be re-enabled
	const enableScroll = () => {
		applyStyles({
			position: "relative",
			top: "",
			left: "",
			right: "",
			bottom: "",
			overflow: "unset",
		});

		// Scroll back to original vertical scroll position
		if (scrollPosition) {
			window.scrollTo(0, scrollPosition);
		}
	};

	// Call corresponding function after timeout according to status of enabled prop
	useEffect(() => {
		const toggleScroll = () => {
			if (enabled) {
				disableScroll();
			} else {
				enableScroll();
			}
		};

		const scrollTimeout = setTimeout(toggleScroll, timeout);

		return () => {
			clearTimeout(scrollTimeout);
			enableScroll();
		};
	}, [enabled, timeout]);
};

export default useDisableMainScroll;
